import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/AuthService';

// Types
interface User {
  id: string;
  username: string;
  email: string;
  full_name: string;
  role: string;
  status: string;
  created_at?: string;
  updated_at?: string;
  last_login?: string;
}

interface LoginUser {
  id: string;
  username: string;
  email: string;
  full_name: string;
  role: string;
  status: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  loginWithSSO: () => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  hasRole: (role: string) => boolean;
  refreshUser: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      if (authService.isAuthenticated()) {
        const currentUser = await authService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
        } else {
          authService.clearAuth();
        }
      }
    } catch (error) {
      console.error('Auth initialization failed:', error);
      authService.clearAuth();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      const response = await authService.login({ username, password });
      
      if (response) {
        // Convert LoginUser to User by adding optional fields
        const userWithDefaults: User = {
          ...response.user,
          created_at: undefined,
          updated_at: undefined,
          last_login: undefined
        };
        setUser(userWithDefaults);
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginWithSSO = async (): Promise<void> => {
    try {
      await authService.loginWithSSO();
    } catch (error) {
      console.error('SSO login failed:', error);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setLoading(true);
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setUser(null);
      authService.clearAuth();
    }
  };

  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'admin';
  
  const hasRole = (role: string): boolean => {
    return user?.role === role;
  };

  const contextValue: AuthContextType = {
    user,
    loading,
    login,
    loginWithSSO,
    logout,
    isAuthenticated,
    isAdmin,
    hasRole,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext;
